import { isEmpty, isEqual, isNil, omitBy } from "lodash";

import {
  ITEM_CARD_DRAG_HANDLE_PADDING_LEFT,
  ITEM_CARD_DRAG_HANDLE_PADDING_RIGHT,
  ITEM_CARD_EVENT_ROW_EDIT_MODE_PADDING_X,
  ITEM_CARD_EVENT_ROW_PADDING_LEFT,
  ITEM_CARD_EVENT_ROW_PADDING_RIGHT,
  ITEM_CARD_NARRATIVE_MARGIN_TOP,
  RELATIONSHIP_EVENT_INDENT_PADDING,
  SUBWAY_CARD_TEXT_WIDTH
} from "../../../../constants";
import Dimensions, { scalePixelsToFontSize } from "../../../../constants/styles/dimensions";
import { calcIdentifierSize, calcSingleLineTextSize, calcTextSize } from "../../../../utils/calcTextSize";
import { getLabelWithUntitled } from "../../../../utils/labelUtils";
import { isDateValid } from "../../../definition/calendar/dateFunctions";
import { stringFromDateRange } from "../../../definition/calendar/dateFunctions/stringFromDate";
import { areTimespansSame, stringFromTimespan } from "../../../definition/calendar/dateFunctions/timespanFunctions";

const { MAX_ITEM_TEXT_WIDTH } = Dimensions;

const areDateValuesSame = (newItem, prevItem) =>
  newItem && prevItem && newItem.startDate === prevItem.startDate && newItem.endDate === prevItem.endDate;

const areEquivalent = (newValue, prevValue) => {
  const a = newValue && newValue.toJS ? newValue.toJS() : newValue;
  const b = prevValue && prevValue.toJS ? prevValue.toJS() : prevValue;
  return isEqual(a, b);
};

export const getDateTextSizes = (displayItem, calendar, todayDate, fontSize, widthMap) => {
  let dateStr = "";
  let durationStr = "";
  if (isDateValid(displayItem.startDate)) {
    const options = {
      dateFormat: calendar.dateFormat
    };
    const hasUncertainStart = isDateValid(displayItem.latestStartDate);
    const hasUncertainEnd =
      displayItem.durationToEarliestEnd && !areTimespansSame(displayItem.durationToEarliestEnd, displayItem.duration);

    dateStr = stringFromDateRange(
      calendar,
      displayItem.startDate,
      displayItem.endDate,
      options,
      true,
      hasUncertainStart,
      hasUncertainEnd,
      displayItem.ongoing,
      displayItem.hasOngoingDescendants,
      todayDate
    );

    durationStr =
      displayItem.endDate.timestamp !== displayItem.startDate.timestamp && !isNil(displayItem.duration)
        ? !isNil(displayItem.duration.label)
          ? displayItem.duration.label
          : stringFromTimespan(displayItem.duration)
        : "";
  }
  const dateTextSize = calcSingleLineTextSize(dateStr, fontSize, widthMap);
  const durationTextSize = calcSingleLineTextSize(durationStr, fontSize, widthMap);
  return { dateTextSize, durationTextSize };
};

export const getItemCardNestedTextWidth = (maxWidth, fontSize, superDepth) => {
  const {
    ITEM_CARD_EVENT_DOT_MARGIN_X,
    ITEM_CARD_EVENT_DOT_SIZE,
    ITEM_CARD_ARROW_PADDING_LEFT,
    ITEM_CARD_ARROW_PADDING_RIGHT,
    ITEM_CARD_ARROW_SIZE,
    ITEM_CARD_INDENT_PADDING,
    ITEM_CARD_EVENT_DRAG_HANDLE_SIZE
  } = Dimensions;
  const rawContentsPadding =
    ITEM_CARD_EVENT_DOT_MARGIN_X * 2 +
    ITEM_CARD_EVENT_DOT_SIZE +
    ITEM_CARD_ARROW_PADDING_LEFT +
    ITEM_CARD_ARROW_PADDING_RIGHT +
    ITEM_CARD_ARROW_SIZE +
    ITEM_CARD_EVENT_DRAG_HANDLE_SIZE;

  const rowPadding =
    ITEM_CARD_EVENT_ROW_PADDING_LEFT +
    ITEM_CARD_EVENT_ROW_EDIT_MODE_PADDING_X +
    ITEM_CARD_DRAG_HANDLE_PADDING_LEFT +
    ITEM_CARD_DRAG_HANDLE_PADDING_RIGHT +
    (superDepth > 0 ? ITEM_CARD_INDENT_PADDING * superDepth : 0) +
    ITEM_CARD_EVENT_ROW_PADDING_RIGHT;

  return maxWidth - rowPadding - scalePixelsToFontSize(rawContentsPadding, fontSize);
};

// Recalculates any text sizes that need to be recalculated
const updateItemTextSizes = (
  state,
  prevState,
  calendar,
  todayDate,
  newFontSize,
  prevFontSize,
  newWidthMap,
  prevWidthMap,
  newSubwayLineLimit,
  prevSubwayLineLimit,
  newMobileWidth = null,
  prevMobileWidth = null,
  newIsMobilePortraitMode = false,
  prevIsMobilePortraitMode = false,
  newMobileShowDragHandles = false,
  prevMobileShowDragHandles = false
) => {
  // We will just loop through each displayItem and look to see if any particular parts need to be updated
  // It may be possible to work out from actions exactly which items we need to look at, but with changes to nesting
  // and narrative position etc. this starts to get complex.

  let maxTextWidth = scalePixelsToFontSize(MAX_ITEM_TEXT_WIDTH, newFontSize);

  let textWidthChanged = false;

  //#!if mobile === true || harvard === true
  if (newIsMobilePortraitMode) {
    maxTextWidth = newMobileWidth;
    textWidthChanged = newMobileWidth !== prevMobileWidth || newMobileShowDragHandles !== prevMobileShowDragHandles;
  } else {
    textWidthChanged = newIsMobilePortraitMode !== prevIsMobilePortraitMode;
  }
  //#!endif

  // If fontSize or widthMap has changed, then we always have to recalculate our text
  const forceRecalculation = newFontSize !== prevFontSize || newWidthMap !== prevWidthMap || textWidthChanged;

  const hasSubwayLimitChanged = newSubwayLineLimit !== prevSubwayLineLimit;

  const SCALED_RELATIONSHIP_EVENT_INDENT_PADDING = scalePixelsToFontSize(
    RELATIONSHIP_EVENT_INDENT_PADDING,
    newFontSize
  );
  const SCALED_SUBWAY_TEXT_WIDTH = scalePixelsToFontSize(SUBWAY_CARD_TEXT_WIDTH, newFontSize);

  const getNarrativeTextSize = (displayItem, fontSize, widthMap) => {
    const label = displayItem.narrativeDisplayLabel ? displayItem.narrativeDisplayLabel : "";
    let size = calcSingleLineTextSize(label, fontSize, widthMap);
    // Add the margin
    const marginTop = scalePixelsToFontSize(ITEM_CARD_NARRATIVE_MARGIN_TOP, fontSize);
    size = size.updateIn(["size", "height"], h => (h > 0 ? h + marginTop : h));
    return size;
  };

  return state.withMutations(s => {
    state.forEach((newItem, itemId) => {
      const prevItem = prevState.get(itemId);

      // If nothing has changed, skip
      if (!forceRecalculation && !hasSubwayLimitChanged && newItem === prevItem) {
        return;
      }

      let changes = {};

      const newNarrativeDepth = newItem.narrativePosition ? newItem.narrativePosition.size - 1 : 0;
      const prevNarrativeDepth = prevItem && prevItem.narrativePosition ? prevItem.narrativePosition.size - 1 : 0;
      const newNarrativeHasChildren = newItem.narrativeHasChildren;
      const prevNarrativeHasChildren = prevItem && prevItem.narrativeHasChildren;

      // Has the label changed?
      const newLabel = getLabelWithUntitled(newItem.label);
      const prevLabel = prevItem ? getLabelWithUntitled(prevItem.label) : null;
      const hasLabelChanged = newLabel !== prevLabel;
      const hasSummaryChanged = !prevItem || newItem.summary !== prevItem.summary;
      const hasSuperDepthChanged = !prevItem || newItem.superDepth !== prevItem.superDepth;
      const hasChildrenChanged = !prevItem || Boolean(newItem.children.size) !== Boolean(prevItem.children.size);
      const hasNarrativeDepthChanged = !prevItem || newNarrativeDepth !== prevNarrativeDepth;
      const hasNarrativeChildrenChanged = !prevItem || newNarrativeHasChildren !== prevNarrativeHasChildren;
      const hasNarrativeDisplayLabelChanged =
        !prevItem || newItem.narrativeDisplayLabel !== prevItem.narrativeDisplayLabel;

      // ---------- Calculations shared across all devices and orientations, using the passed in width --------

      // Identifier Size
      if (forceRecalculation || !prevItem || newItem.displayId !== prevItem.displayId) {
        changes.identifierSize = calcIdentifierSize(newItem.displayId, false, newFontSize, newWidthMap);
      }

      // Date Text Size and Duration Text Size
      if (forceRecalculation || !areDateValuesSame(newItem, prevItem)) {
        Object.assign(changes, getDateTextSizes(newItem, calendar, todayDate, newFontSize, newWidthMap, maxTextWidth));
      }

      // Calculate Narrative Text Size
      if (forceRecalculation || hasNarrativeDisplayLabelChanged) {
        changes.narrativeTextSize = getNarrativeTextSize(newItem, newFontSize, newWidthMap);
      }

      // ---------- Calculations for Desktop, Tablet, and Mobile Landscape only --------
      if (!newIsMobilePortraitMode) {
        if (forceRecalculation || hasLabelChanged) {
          changes.titleMetrics = calcTextSize(newLabel, newFontSize, newWidthMap, maxTextWidth);
        }
        if (forceRecalculation || hasLabelChanged || hasSuperDepthChanged || hasChildrenChanged) {
          const nestedTextWidth = maxTextWidth - newItem.superDepth * SCALED_RELATIONSHIP_EVENT_INDENT_PADDING;
          changes.nestedTitleMetrics = calcTextSize(newLabel, newFontSize, newWidthMap, nestedTextWidth);
        }
        if (forceRecalculation || hasLabelChanged || hasSubwayLimitChanged) {
          changes.subwayTitleMetrics = calcTextSize(
            newLabel,
            newFontSize,
            newWidthMap,
            SCALED_SUBWAY_TEXT_WIDTH,
            false,
            newSubwayLineLimit
          );
        }
        if (forceRecalculation || hasLabelChanged || hasNarrativeDepthChanged) {
          const narrativeNestedTextWidth = maxTextWidth - newNarrativeDepth * SCALED_RELATIONSHIP_EVENT_INDENT_PADDING;
          changes.narrativeNestedTitleMetrics =
            newNarrativeDepth > 0
              ? calcTextSize(newLabel, newFontSize, newWidthMap, narrativeNestedTextWidth)
              : changes.titleMetrics;
        }
        if (forceRecalculation || hasSummaryChanged) {
          changes.summaryMetrics = calcTextSize(newItem.summary, newFontSize, newWidthMap, maxTextWidth);
        }
      }

      // ---------- Calculations for Mobile portrait only --------

      //#!if mobile === true || harvard === true
      if (newIsMobilePortraitMode) {
        const superDepth = newItem.superDepth;
        const hasChildren = newItem.children.size > 0;

        const nestedTextWidth = getItemCardNestedTextWidth(
          maxTextWidth,
          newFontSize,
          superDepth,
          hasChildren,
          newMobileShowDragHandles
        );
        const nestedNarrativeTextWidth = getItemCardNestedTextWidth(
          maxTextWidth,
          newFontSize,
          newNarrativeDepth,
          newNarrativeHasChildren,
          newMobileShowDragHandles
        );

        if (forceRecalculation || hasLabelChanged || hasSuperDepthChanged || hasChildrenChanged) {
          changes.itemCardNestedTitleMetrics = calcTextSize(newLabel, newFontSize, newWidthMap, nestedTextWidth);
        }

        if (forceRecalculation || hasLabelChanged || hasNarrativeDepthChanged || hasNarrativeChildrenChanged) {
          changes.itemCardNarrativeNestedTitleMetrics = calcTextSize(
            newLabel,
            newFontSize,
            newWidthMap,
            nestedNarrativeTextWidth
          );
        }

        if (forceRecalculation || hasSummaryChanged || hasSuperDepthChanged || hasChildrenChanged) {
          changes.itemCardNestedSummaryMetrics = calcTextSize(
            newItem.summary,
            newFontSize,
            newWidthMap,
            nestedTextWidth
          );
        }

        if (forceRecalculation || hasSummaryChanged || hasNarrativeDepthChanged || hasNarrativeChildrenChanged) {
          changes.itemCardNarrativeNestedSummaryMetrics = calcTextSize(
            newItem.summary,
            newFontSize,
            newWidthMap,
            nestedNarrativeTextWidth
          );
        }
      }
      //#!endif

      // If this is not our first calculation for this item, then let's avoid unnecessary updates
      // by stripping out any values that are identical to their last ones
      // Strip out any changes that are identical to stored values
      if (prevItem) {
        changes = omitBy(changes, (value, key) => {
          return areEquivalent(value, newItem.get(key));
        });
      }

      // Now apply any changes we have found
      if (!isEmpty(changes)) {
        s.update(itemId, d => d.merge(changes));
      }
    });
  });
};

export default updateItemTextSizes;
